import * as React from 'react';
import { Card, Loading, Table, Input, Page, notify } from 'sha-el-design';
import { nestedAccess } from 'sha-el-design/lib/helpers';
import * as moment from 'moment';
import { GraphqlQuery } from 'requestapijs';
import { Colums } from 'sha-el-design/lib/components/Table/Table';
import { Link } from 'react-router-dom';
import { Pagination } from '../Common/Pagination';
import {
  UserDetailNodeEdge,
  UserDetailNodeConnection,
} from '../../../schema';
import { UserDetailDropdown } from '../Common/UserDetailDropdown';
import { summarisedReportQuery } from './constants';
import { num } from './deep';
import {
  FMT, PRESETS, RangePresets, activePresetKey,
  statTile, connectRate, rateMeter, groupNum, decorateRows, clickedRowIndex,
} from './reportBits';
import { MdArrowBack, MdHome, MdChevronRight } from 'react-icons/md';
import '../Common/listPageStyles';

export class SummarisedCallHistory extends React.Component<{}, State> {

  constructor(props: {}) {
    super(props);
    // Defaulting both ends to TODAY meant the report opened empty on a
    // database whose newest call is months old, with nothing on screen to say
    // why. Open on the last 30 days instead.
    const from = moment().subtract(29, 'days').format(FMT);
    const to = moment().format(FMT);
    this.state = {
      fromDate: from,
      toDate: to,
      caller: null,
      // What the query is actually running. Split from the draft above so a
      // report over a wide range is fetched when the user asks for it, not on
      // every keystroke of a date they are still typing.
      appliedFrom: from,
      appliedTo: to,
      appliedCaller: null,
      after: '',
    };
  }

  rangeIsValid = () => {
    const { fromDate, toDate } = this.state;
    return !!fromDate && !!toDate && !moment(fromDate).isAfter(moment(toDate));
  }

  isDirty = () => {
    const { fromDate, toDate, caller, appliedFrom, appliedTo, appliedCaller } = this.state;
    return fromDate !== appliedFrom
      || toDate !== appliedTo
      || nestedAccess(caller, 'node', 'id') !== nestedAccess(appliedCaller, 'node', 'id');
  }

  applyPreset = (preset: typeof PRESETS[0]) => {
    const [a, b] = preset.range();
    const fromDate = a.format(FMT);
    const toDate = b.format(FMT);
    // A preset is an explicit choice, so it searches immediately -- no second
    // click on Search.
    this.setState({
      fromDate, toDate,
      appliedFrom: fromDate, appliedTo: toDate,
      appliedCaller: this.state.caller,
      after: '',
    });
  }

  search = () => {
    if (!this.rangeIsValid()) {
      return notify({
        type: 'error',
        title: 'Check the dates',
        message: 'From Date must be on or before To Date.',
        duration: 3000,
      });
    }
    const { fromDate, toDate, caller } = this.state;
    this.setState({
      appliedFrom: fromDate,
      appliedTo: toDate,
      appliedCaller: caller,
      after: '',   // a new search starts at page 1
    });
  }

  onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { this.search(); }
  }

  goToCaller = (id: string) => {
    (this.props as any).history
      ? (this.props as any).history.push(`/dashboard/call_history_report/${id}`)
      : window.location.assign(`/dashboard/call_history_report/${id}`);
  }

  count = (value: number, variant?: 'alert') => (
    <span
      className={
        'cs-rep__num' +
        (value ? (variant === 'alert' ? ' cs-rep__num--alert' : '') : ' cs-rep__num--zero')
      }
    >
      {groupNum(value)}
    </span>
  )

  columns = (): Array<Colums<UserDetailNodeEdge>> => [{
    key: 'caller',
    header: 'Caller',
    render: (text, obj) => (
      <div>
        <Link className='cs-q__name' to={`/dashboard/call_history_report/${obj.node.id}`}>
          {nestedAccess(obj.node, 'employee', 'name') || 'Unnamed caller'}
        </Link>
        <p className='cs-q__desc'>
          {nestedAccess(obj.node, 'employee', 'townid', 'name') || 'No town'}
        </p>
      </div>
    ),
  }, {
    key: 'total_assignment',
    header: 'Allocations',
    render: (text, obj) => this.count(num(obj.node.totalAssignment)),
  }, {
    key: 'call_answered',
    header: 'Answered',
    render: (text, obj) => this.count(num(obj.node.callAnswered)),
  }, {
    key: 'not_answered',
    header: 'No Reply',
    render: (text, obj) => this.count(num(obj.node.notAnswered)),
  }, {
    key: 'wrong_number',
    header: 'Wrong Number',
    render: (text, obj) => this.count(num(obj.node.wrongNumber)),
  }, {
    key: 'submit_with_alert',
    header: 'Alerts',
    render: (text, obj) => this.count(num(obj.node.submitWithAlert), 'alert'),
  }, {
    key: 'connect_rate',
    header: 'Connect Rate',
    render: (text, obj) => rateMeter(connectRate(
      num(obj.node.callAnswered), num(obj.node.notAnswered), num(obj.node.wrongNumber))),
  }, {
    key: 'go',
    header: '',
    render: () => <span className='cs-rep__go'><MdChevronRight /></span>,
  }]

  // sha-el's Table has no row-props hook, so the stacked-card labels and the
  // whole-row click are wired up on the rendered DOM after paint. Cheap, and
  // it keeps the table component itself untouched.
  private tableRef: HTMLDivElement = null;

  // Deliberately NOT componentDidUpdate: the table lives inside GraphqlQuery's
  // render callback, and GraphqlQuery holds its own state -- when the response
  // arrives it re-renders itself without re-rendering this component, so the
  // parent's lifecycle hooks never fire for the render that first produces
  // rows. Decoration is driven from the ref instead (see render), which must
  // stay an INLINE arrow: React only re-invokes a ref callback when its
  // identity changes, so a bound method would fire once on mount and never
  // again -- leaving rows added by pagination undecorated.

  totals = (edges: any[]) => edges.reduce((acc, e) => {
    const n = e.node;
    acc.answered += num(n.callAnswered);
    acc.noReply += num(n.notAnswered);
    acc.wrong += num(n.wrongNumber);
    acc.alerts += num(n.submitWithAlert);
    acc.allocations += num(n.totalAssignment);
    return acc;
  }, { answered: 0, noReply: 0, wrong: 0, alerts: 0, allocations: 0 })

  stats = (edges: any[], hasNextPage: boolean) => {
    const t = this.totals(edges);
    const calls = t.answered + t.noReply + t.wrong;
    const rate = calls ? Math.round((t.answered / calls) * 100) : 0;
    const scope = hasNextPage ? 'callers on this page' : `${edges.length} caller${edges.length === 1 ? '' : 's'}`;
    return (
      <div className='cs-stats'>
        {statTile('calls', 'Calls placed', groupNum(calls), `across ${scope}`)}
        {statTile('answered', 'Answered', groupNum(t.answered),
          `${groupNum(t.noReply)} no reply · ${groupNum(t.wrong)} wrong number`)}
        {statTile('rate', 'Connect rate', `${rate}%`, 'of dialled numbers answered')}
        {statTile('alert', 'Allocations worked', groupNum(t.allocations),
          t.alerts ? `${groupNum(t.alerts)} response${t.alerts === 1 ? '' : 's'} flagged`
                   : 'no responses flagged')}
      </div>
    );
  }

  breadcrumbs = () => ({
    seperator: '/',
    paths: [
      { text: <MdHome />, link: '/dashboard' },
      { text: 'Call History Report', link: '/dashboard/call_history_report' },
    ].map((v) => ({
      render: () => (
        <Link key={v.link} to={v.link}>
          {v.text}
        </Link>
      ),
    })),
  })

  render() {
    const {
      fromDate, toDate, caller,
      appliedFrom, appliedTo, appliedCaller, after,
    } = this.state;
    const validRange = this.rangeIsValid();
    const dirty = this.isDirty();
    const active = activePresetKey(appliedFrom, appliedTo);
    return (
      <div className='cs-list'>
        <Page
          title={'Call History Report'}
          breadcrumbs={this.breadcrumbs()}
          backIcon={
            <Link to={'/dashboard'}>
              <MdArrowBack />
            </Link>
          }
        >
          <Card>
            <RangePresets activeKey={active} onPick={this.applyPreset} />

            {/* Every control is wrapped in .cs-rep__field so it occupies
                exactly one grid cell -- sha-el's Input renders a container
                AND a help div as siblings, which otherwise become two
                separate columns. */}
            <div className='cs-rep__filters' onKeyDown={this.onKeyDown}>
              <div className='cs-rep__field'>
                <Input
                  type='date'
                  label='From Date'
                  value={fromDate}
                  onChange={(e) => this.setState({ fromDate: e.target.value })}
                />
              </div>
              <div className='cs-rep__field'>
                <Input
                  type='date'
                  label='To Date'
                  value={toDate}
                  onChange={(e) => this.setState({ toDate: e.target.value })}
                />
              </div>
              <div className='cs-rep__field'>
                <UserDetailDropdown
                  mode='single'
                  value={caller}
                  onChange={(v) => this.setState({ caller: v })}
                  uniqueIdentifier={(v) => v && v.node.id}
                  displayProp={(v) => (v && nestedAccess(v, 'node', 'employee', 'name')) || ''}
                  label='Caller (all by default)'
                  inputProps={{}}
                  userType='CALLER'
                />
              </div>
              <button
                type='button'
                className='cs-rep__search'
                onClick={this.search}
                disabled={!validRange}
              >
                Search
              </button>
            </div>

            <p className='cs-rep__scope'>
              <span>
                Calls placed between <b>{moment(appliedFrom).format('D MMM YYYY')}</b> and{' '}
                <b>{moment(appliedTo).format('D MMM YYYY')}</b>
                {appliedCaller
                  ? <> for <b>{nestedAccess(appliedCaller, 'node', 'employee', 'name')}</b></>
                  : ' for all callers'}.
              </span>
              {!validRange && <span className='cs-rep__warn'>From Date is after To Date</span>}
              {validRange && dirty && <span className='cs-rep__dirty'>Press Search to apply</span>}
            </p>

            <GraphqlQuery
              key='query'
              queryString={summarisedReportQuery}
              variables={{
                dateRange: `${appliedFrom},${appliedTo}`,
                userId: nestedAccess(appliedCaller, 'node', 'id'),
                after,
              }}
              render={(response: { allUserDetails: UserDetailNodeConnection }, error, loading) => {
                // requestapijs only reports transport failures through
                // `error`; a resolver error arrives as a 200 with null data,
                // so guard on the payload as well or .edges throws.
                const edges = nestedAccess(response, 'allUserDetails', 'edges');
                if (error || (!loading && !edges)) {
                  return (
                    <div className='cs-list__empty'>
                      <h4>Could not load the report</h4>
                      <p>Check the date range and try again.</p>
                    </div>
                  );
                }
                const hasNext = !!nestedAccess(response, 'allUserDetails', 'pageInfo', 'hasNextPage');
                return (
                  <Loading
                    isLoading={loading}
                    render={() => (
                      <>
                        {edges.length ? (
                          <>
                            {this.stats(edges, hasNext)}
                            <div
                              className='cs-list--stack'
                              ref={(el) => { this.tableRef = el; if (el) { decorateRows(el, { clickable: true }); } }}
                              onClick={(e) => {
                                const i = clickedRowIndex(this.tableRef, e.target as HTMLElement);
                                const node = i >= 0 && edges[i] && edges[i].node;
                                if (node) { this.goToCaller(node.id); }
                              }}
                            >
                              <Table
                                data={edges}
                                columns={this.columns()}
                              />
                            </div>
                          </>
                        ) : (
                          <div className='cs-list__empty'>
                            <h4>No calls in this period</h4>
                            <p>
                              No caller placed a call between{' '}
                              {moment(appliedFrom).format('D MMM YYYY')} and{' '}
                              {moment(appliedTo).format('D MMM YYYY')}. Try a wider range —{' '}
                              <b>All time</b> shows everything on record. Note that a caller who was
                              given work but never dialled does not appear here at all.
                            </p>
                          </div>
                        )}
                        <Pagination
                          key={`${appliedFrom}|${appliedTo}|${nestedAccess(appliedCaller, 'node', 'id') || ''}`}
                          endCursor={nestedAccess(response, 'allUserDetails', 'pageInfo', 'endCursor')}
                          hasNextPage={hasNext}
                          after={(afterValue) => this.setState({ after: afterValue })}
                        />
                      </>
                    )}
                  />
                );
              }}
            />
          </Card>
        </Page>
      </div>
    );
  }
}

interface State {
  fromDate: string;
  toDate: string;
  caller: UserDetailNodeEdge;
  appliedFrom: string;
  appliedTo: string;
  appliedCaller: UserDetailNodeEdge;
  after: string;
}
